import { useMemo, useRef, useState } from 'react'
import { useQueries } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Combobox } from '@/components/common/Combobox'
import { useFormSchema, formAdminKeys } from '@/features/admin/forms/hooks'
import { PRESENTATIONAL_TYPES } from '@/features/admin/forms/types'
import { getFormSchema } from '@/features/admin/forms/api'
import { useConfigurationComposition, useProducts } from '@/features/product/hooks'
import { useIntegrationSources } from '@/features/admin/integrations/hooks'
import { formatError } from '@/features/admin/stages/components/formatError'
import { useReplaceSourceMappings, useSourceMappings } from '../sourceMappingHooks'
import type { SourceMappingRow } from '../sourceMappingApi'

/** A selectable target = either a contract field, or a product field for a specific product of this config. */
type TargetOption = {
  /** Stable encoded value for the <select>. */
  key: string
  label: string
  targetKind: SourceMappingRow['targetKind']
  fieldCode: string
  productCode: string | null
}

const CONTRACT_KEY = (fieldCode: string) => `CONTRACT::${fieldCode}`
const PRODUCT_KEY = (productCode: string, fieldCode: string) => `PRODUCT::${productCode}::${fieldCode}`

function rowKey(row: SourceMappingRow): string {
  return row.targetKind === 'PRODUCT_FIELD' && row.productCode
    ? PRODUCT_KEY(row.productCode, row.fieldCode)
    : CONTRACT_KEY(row.fieldCode)
}

/**
 * Panel: per-(configuration, source) mapping of inbound payload dot-paths to contract/product fields.
 * Source select + editable rows (dot-path + target dropdown), import/export JSON, save = PUT replace.
 */
export function SourceMappingPanel({ configurationCode }: { configurationCode: string }) {
  const sourcesQ = useIntegrationSources()
  const contractSchemaQ = useFormSchema('contract')
  const compositionQ = useConfigurationComposition(configurationCode)
  const productsQ = useProducts()

  // Per-product schemas: each product/{code} schema only contains the sections (and thus
  // the fields) attached to THAT product. We can't call useFormSchema in a loop (rules of
  // hooks) because the product count is dynamic, so fan out via useQueries reusing the same
  // query key shape + api fn that useFormSchema uses.
  const productCodes = useMemo(() => compositionQ.data ?? [], [compositionQ.data])
  const perProductQ = useQueries({
    queries: productCodes.map(pc => ({
      queryKey: formAdminKeys.schema(`product/${pc}`),
      queryFn: () => getFormSchema(`product/${pc}`),
    })),
  })

  const [sourceCode, setSourceCode] = useState('')
  const [rows, setRows] = useState<SourceMappingRow[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const mappingsQ = useSourceMappings(configurationCode, sourceCode)
  const replace = useReplaceSourceMappings(configurationCode, sourceCode)

  // Per-product schema data, keyed by product code (only loaded entries). Mapped out of the
  // useQueries result so the options useMemo has a stable, primitive-ish dependency.
  const perProductData = useMemo(
    () =>
      productCodes.map((pc, i) => ({ productCode: pc, schema: perProductQ[i]?.data })),
    // perProductQ identity changes each render; gate on the resolved data instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productCodes, ...perProductQ.map(q => q.data)],
  )

  // Build target options once schemas + composition are loaded.
  const { options, optionByKey } = useMemo(() => {
    const opts: TargetOption[] = []

    const contractFields = contractSchemaQ.data?.fields ?? []
    for (const f of contractFields) {
      if (PRESENTATIONAL_TYPES.includes(f.fieldType)) continue // separatory/naglowki/spacery - nie mapowalne
      opts.push({
        key: CONTRACT_KEY(f.fieldCode),
        label: `Umowa: ${f.labelPl} (${f.fieldCode})`,
        targetKind: 'CONTRACT_FIELD',
        fieldCode: f.fieldCode,
        productCode: null,
      })
    }

    const labelByCode = new Map((productsQ.data ?? []).map(p => [p.code, p.labelPl]))
    for (const { productCode: pc, schema } of perProductData) {
      if (!schema) continue
      const pLabel = labelByCode.get(pc) ?? pc
      // Fields actually placed in a section attached to THIS product: derive the placed
      // field ids from the product-scoped schema's placements, then intersect with .fields.
      const placedFieldIds = new Set(schema.placements.map(p => p.fieldId))
      const productFields = schema.fields.filter(f => placedFieldIds.has(f.id) && !PRESENTATIONAL_TYPES.includes(f.fieldType))
      for (const f of productFields) {
        opts.push({
          key: PRODUCT_KEY(pc, f.fieldCode),
          label: `Produkt ${pLabel}: ${f.labelPl} (${f.fieldCode})`,
          targetKind: 'PRODUCT_FIELD',
          fieldCode: f.fieldCode,
          productCode: pc,
        })
      }
    }

    return { options: opts, optionByKey: new Map(opts.map(o => [o.key, o])) }
  }, [contractSchemaQ.data, perProductData, productsQ.data])

  // Reset local rows when a new source's mappings arrive.
  const loadedSig = mappingsQ.dataUpdatedAt
  const lastLoadedRef = useRef<number>(0)
  if (mappingsQ.data && loadedSig !== lastLoadedRef.current) {
    lastLoadedRef.current = loadedSig
    setRows(mappingsQ.data)
  }

  function updateRow(index: number, patch: Partial<SourceMappingRow>) {
    setRows(prev => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  }

  function setRowTarget(index: number, optionKey: string) {
    const opt = optionByKey.get(optionKey)
    if (!opt) return
    updateRow(index, { targetKind: opt.targetKind, fieldCode: opt.fieldCode, productCode: opt.productCode })
  }

  function addRow() {
    setRows(prev => [...prev, { dotPath: '', targetKind: 'CONTRACT_FIELD', fieldCode: '', productCode: null }])
  }

  function deleteRow(index: number) {
    setRows(prev => prev.filter((_, i) => i !== index))
  }

  function handleSave() {
    const invalid = rows.find(r => !r.dotPath.trim() || !r.fieldCode)
    if (invalid) {
      toast.error('Uzupełnij ścieżkę i pole docelowe we wszystkich wierszach.')
      return
    }
    replace.mutate(rows, {
      onSuccess: saved => {
        setRows(saved)
        toast.success('Mapowania zapisane.')
      },
      onError: e => toast.error(formatError(e)),
    })
  }

  function handleExport() {
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `mapowania-${configurationCode}-${sourceCode}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  function handleImport(file: File) {
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result))
        if (!Array.isArray(parsed)) throw new Error('not an array')
        const valid: SourceMappingRow[] = []
        let dropped = 0
        for (const raw of parsed as unknown[]) {
          const r = raw as Partial<SourceMappingRow>
          if (typeof r?.dotPath !== 'string' || typeof r?.fieldCode !== 'string') {
            dropped++
            continue
          }
          const targetKind = r.targetKind === 'PRODUCT_FIELD' ? 'PRODUCT_FIELD' : 'CONTRACT_FIELD'
          const productCode = targetKind === 'PRODUCT_FIELD' ? r.productCode ?? null : null
          const candidate: SourceMappingRow = { dotPath: r.dotPath, targetKind, fieldCode: r.fieldCode, productCode }
          // Drop rows whose target isn't a valid (field/product) combo for this configuration.
          if (!optionByKey.has(rowKey(candidate))) {
            dropped++
            continue
          }
          valid.push(candidate)
        }
        setRows(valid)
        toast.success(
          dropped > 0
            ? `Zaimportowano ${valid.length} wierszy, pominięto ${dropped} niepasujących.`
            : `Zaimportowano ${valid.length} wierszy.`,
        )
      } catch {
        toast.error('Nieprawidłowy plik JSON.')
      }
    }
    reader.readAsText(file)
  }

  const sources = sourcesQ.data ?? []
  const schemasLoading =
    contractSchemaQ.isLoading || compositionQ.isLoading || perProductQ.some(q => q.isLoading)

  return (
    <div className="flex flex-col gap-3 text-sm">
      <p className="text-xs text-slate-500">
        Mapowanie pól przychodzących webhooków na pola umowy i produktów tej konfiguracji. Dla wybranego źródła
        wskaż ścieżkę w payloadzie (dot-path) i pole docelowe.
      </p>

      <div className="flex items-center gap-2">
        <label className="text-xs text-slate-600">Źródło:</label>
        <select
          className="rounded border px-2 py-1 text-sm"
          value={sourceCode}
          onChange={e => setSourceCode(e.target.value)}
        >
          <option value="">— wybierz źródło —</option>
          {sources.map(s => (
            <option key={s.code} value={s.code}>
              {s.name} ({s.code}){s.isActive ? '' : ' — nieaktywne'}
            </option>
          ))}
        </select>
      </div>

      {!sourceCode ? (
        <p className="text-xs text-slate-400">Wybierz źródło integracji, aby edytować mapowania.</p>
      ) : mappingsQ.isLoading || schemasLoading ? (
        <div>Ładowanie mapowań...</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={addRow}
              className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
            >
              + dodaj wiersz
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
            >
              Eksportuj JSON
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
            >
              Importuj JSON
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={e => {
                const f = e.target.files?.[0]
                if (f) handleImport(f)
                e.target.value = ''
              }}
            />
          </div>

          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b text-slate-500">
                <th className="py-1 pr-2 font-medium">Ścieżka w payloadzie (dot-path)</th>
                <th className="py-1 pr-2 font-medium">Pole docelowe</th>
                <th className="py-1 w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-3 text-slate-400">
                    Brak mapowań. Dodaj wiersz.
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={i} className="border-b align-top">
                    <td className="py-1 pr-2">
                      <input
                        type="text"
                        value={row.dotPath}
                        onChange={e => updateRow(i, { dotPath: e.target.value })}
                        placeholder="np. data.customer.email"
                        className="w-full rounded border px-2 py-1 font-mono"
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <Combobox<string>
                        value={row.fieldCode ? rowKey(row) : null}
                        onChange={v => {
                          if (v) setRowTarget(i, v)
                        }}
                        options={options.map(o => ({ value: o.key, label: o.label }))}
                        placeholder="— wybierz pole —"
                        emptyOptionLabel="— wybierz pole —"
                      />
                    </td>
                    <td className="py-1">
                      <button
                        type="button"
                        onClick={() => deleteRow(i)}
                        className="rounded px-2 py-1 text-red-600 hover:bg-red-50"
                        title="Usuń wiersz"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div>
            <button
              type="button"
              onClick={handleSave}
              disabled={replace.isPending}
              className="rounded bg-sky-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-800 disabled:opacity-50"
            >
              {replace.isPending ? 'Zapisywanie...' : 'Zapisz mapowania'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
